import { createContext, useContext, useEffect, useRef, useState } from "react";
import {
  Routes,
  Route,
  Navigate,
  useNavigate,
  useLocation,
} from "react-router-dom";

import Navbar from "./components/Navbar";
import Sidebar from "./components/Sidebar";
import Overlay from "./components/Overlay";
import Footer from "./components/Footer";

import Dashboard from "./pages/Dashboard";
import ProfileDashboard from "./pages/ProfileDashboard";
import ProfileViewer from "./pages/ProfileViewer";
import ArticleDashboard from "./pages/ArticleDashboard";
import ArticleViewer from "./pages/ArticleViewer";
import JudgmentDashboard from "./pages/JudgmentDashboard";
import JudgmentUploadModal from "./pages/JudgmentUploadModal";
import JudgmentViewer from "./pages/JudgmentViewer";
import Judge from "./pages/Judge";
import Act from "./pages/Act";
import User from "./pages/User";
import Login from "./pages/Login";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import AdminSettings from "./pages/AdminSettings";
import AuditLog from "./pages/AuditLog";
import Journal from "./pages/Journal";
import Maintenance from "./pages/Maintenance";

import { apiFetch } from "./utils/apifetch";

/* ================= AUTH CONTEXT ================= */
const AuthContext = createContext(null);
export const useAuth = () => useContext(AuthContext);

/* ================= PROTECTED ROUTE ================= */
function ProtectedRoute({ children }) {
  const { token, internalLogout } = useAuth();
  const [checking, setChecking] = useState(true);

  useEffect(() => {
    if (!token) {
      setChecking(false);
      return;
    }

    fetch(`/auth/validate`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((res) => {
        if (res.status === 401) internalLogout(false);
      })
      .finally(() => setChecking(false));
  }, [token, internalLogout]);

  if (checking) return null;
  return token ? children : <Navigate to="/login" replace />;
}

/* ================= PERMISSION ROUTE ================= */
// function PermissionRoute({ resource, children }) {
//   const { permissions } = useAuth();
//   return permissions?.[resource]?.length ? (
//     children
//   ) : (
//     <Navigate to="/access-denied" replace />
//   );
// }

function PermissionRoute({ resource, action = "read", children }) {
  const { permissions } = useAuth();
  return permissions?.[resource]?.includes(action)
    ? children
    : <Navigate to="/access-denied" replace />;
}



/* ================= MAIN APP ================= */
export default function App() {
  const navigate = useNavigate();
  const location = useLocation();

  /* ---------- GLOBAL STATE (ALL HOOKS FIRST) ---------- */

  const [appStatus, setAppStatus] = useState(null);

  const [authReady, setAuthReady] = useState(false);
  const [token, setToken] = useState(null);
  const [uid, setUid] = useState(null);
  const [usertype, setUsertype] = useState(null);
  const [permissions, setPermissions] = useState({});

  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isDarkMode, setIsDarkMode] = useState(true);

  const [idleTimeoutMs, setIdleTimeoutMs] = useState(0);
  const idleTimerRef = useRef(null);

  const tabIdRef = useRef(
    sessionStorage.getItem("tab_id") || crypto.randomUUID()
  );

  /* ---------- EFFECTS (NO RETURNS HERE) ---------- */

  /* Maintenance status (PUBLIC, NO AUTH) */
  useEffect(() => {
    fetch(`/api/settings/app-status?_=${Date.now()}`, { cache: "no-store" })
      .then((res) => res.json())
      .then((data) => {
        setAppStatus(data.status || "LIVE NOW");
      })
      .catch(() => {
        setAppStatus("LIVE NOW");
      });
  }, []);

  useEffect(() => {
    sessionStorage.setItem("tab_id", tabIdRef.current);
  }, []);

  const internalLogout = async (broadcast = true) => {
    if (idleTimerRef.current) clearTimeout(idleTimerRef.current);

    // optional backend logout removed
    // if (broadcast) {
    //   try {
    //     const t = localStorage.getItem("token");
    //     if (t) {
    //       await fetch(`/logout`, {
    //         method: "POST",
    //         headers: { Authorization: `Bearer ${t}` },
    //       });
    //     }
    //   } catch { }
    // }

    localStorage.removeItem("token");
    localStorage.removeItem("uid");
    localStorage.removeItem("usertype");
    if (uid) {
      localStorage.removeItem(`active_tab_${uid}`);
      localStorage.removeItem(`login_grace_${uid}`);
    }

    sessionStorage.removeItem("tab_id");
    setToken(null);
    setUid(null);
    setUsertype(null);
    setPermissions({});
    navigate("/login", { replace: true });
  };

  const logout = () => internalLogout(true);

  /* Auth rehydration */
  useEffect(() => {
    const storedToken = localStorage.getItem("token");
    if (!storedToken) {
      setAuthReady(true);
      return;
    }

    fetch(`/auth/validate`, {
      headers: { Authorization: `Bearer ${storedToken}` },
    })
      .then((res) => {
        if (!res.ok) throw new Error();
        return res.json();
      })
      .then((data) => {
        setToken(storedToken);
        setUid(data.uid);
        setUsertype(data.usertype);
      })
      .catch(() => internalLogout(false))
      .finally(() => setAuthReady(true));
  }, []);

  /* Permissions */
  useEffect(() => {
    if (!token || !uid) {
      setPermissions({});
      return;
    }

    apiFetch(`/users/${uid}/permissions`, {}, logout, navigate)
      .then((res) => res.json())
      .then((data) => setPermissions(data || {}))
      .catch(() => setPermissions({}));
  }, [token, uid]);

  const loadIdleTimeout = async () => {
    try {
      const res = await apiFetch(`/settings/idle-timeout`, {}, logout, navigate);
      const data = await res.json();

      const newTimeout = (data.idleLogoutMinutes || 5) * 60 * 1000;

      setIdleTimeoutMs(newTimeout);

      /* immediately restart idle timer */
      if (idleTimerRef.current) clearTimeout(idleTimerRef.current);

      idleTimerRef.current = setTimeout(() => {
        console.log("Idle timeout reached → logging out");
        internalLogout(true);
      }, newTimeout);

    } catch {
      const fallback = 5 * 60 * 1000;
      setIdleTimeoutMs(fallback);
    }
  };


  useEffect(() => {
    if (!token) return;
    loadIdleTimeout();
  }, [token]);


  /* Idle timer handling */
  useEffect(() => {

    if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
    if (!token || !idleTimeoutMs) return;

    const resetTimer = () => {
      if (idleTimerRef.current) clearTimeout(idleTimerRef.current);

      idleTimerRef.current = setTimeout(() => {
        console.log("Idle timeout reached → logging out");
        internalLogout(true);
      }, idleTimeoutMs);
    };

    const events = [
      "mousemove",
      "mousedown",
      "keypress",
      "scroll",
      "touchstart",
    ];

    events.forEach((event) =>
      window.addEventListener(event, resetTimer)
    );

    resetTimer(); // start timer

    return () => {
      if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
      events.forEach((event) =>
        window.removeEventListener(event, resetTimer)
      );
    };
  }, [token, idleTimeoutMs, internalLogout]);


  /* Theme */
  useEffect(() => {
    const saved = localStorage.getItem("theme");
    if (saved === "dark") setIsDarkMode(true);
  }, []);

  useEffect(() => {
    document.documentElement.setAttribute(
      "data-theme",
      isDarkMode ? "dark" : "light"
    );
    localStorage.setItem("theme", isDarkMode ? "dark" : "light");
  }, [isDarkMode]);

  /* ---------- RENDER DECISION (ONLY HERE) ---------- */

  if (appStatus === null) {
    return <div style={{ height: "100vh" }} />;
  }

  if (appStatus === "UNDER MAINTENANCE") {
    return <Maintenance />;
  }

  if (!authReady) {
    return null;
  }

  const isAuthPage =
    location.pathname === "/login" ||
    location.pathname === "/forgot-password" ||
    location.pathname === "/reset-password";

  const hasPermission = (resource, action = "read") =>
    permissions?.[resource]?.includes(action);

  const canAccess = (resource) =>
    permissions?.[resource]?.includes("read");

  /* ---------- NORMAL APP UI ---------- */

  return (
    <AuthContext.Provider
      value={{
        token,
        uid,
        usertype,
        permissions,
        login: ({ token, uid, usertype }) => {
          localStorage.setItem("token", token);
          localStorage.setItem("uid", uid);
          localStorage.setItem("usertype", usertype);
          setToken(token);
          setUid(uid);
          setUsertype(usertype);
          navigate("/", { replace: true });
        },
        logout,
        internalLogout,
        hasPermission,
        canAccess,
        reloadIdleTimeout: loadIdleTimeout
      }}
    >
      <Navbar
        isLoggedIn={!!token}
        onLogin={() => navigate("/login")}
        onLogout={logout}
        onHamburgerClick={
          isAuthPage ? undefined : () => setIsSidebarOpen((p) => !p)
        }
        isDarkMode={isDarkMode}
        onThemeToggle={
          isAuthPage ? undefined : () => setIsDarkMode((p) => !p)
        }
        disableControls={isAuthPage}
      />

      <div className="container">
        <Sidebar
          isOpen={isSidebarOpen}
          onClose={() => setIsSidebarOpen(false)}
          disabled={isAuthPage}
        />

        {!isAuthPage && (
          <Overlay
            isActive={isSidebarOpen}
            onClick={() => setIsSidebarOpen(false)}
          />
        )}

        <main className="content">
          <Routes>
            {/* ========== AUTH ========== */}
            <Route path="/login" element={<Login />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />

            {/* ========== DASHBOARD ========== */}
            <Route
              path="/"
              element={
                <ProtectedRoute>
                  <Dashboard />
                </ProtectedRoute>
              }
            />

            {/* ========== PROFILE ========== */}
            <Route
              path="/profile"
              element={
                <ProtectedRoute>
                  <PermissionRoute resource="profile" action="read">
                    <ProfileDashboard />
                  </PermissionRoute>
                </ProtectedRoute>
              }
            />
            <Route
              path="/profile/:id"
              element={
                <ProtectedRoute>
                  <PermissionRoute resource="profile" action="read">
                    <ProfileViewer />
                  </PermissionRoute>
                </ProtectedRoute>
              }
            />

            {/* ========== ARTICLE ========== */}
            <Route
              path="/article"
              element={
                <ProtectedRoute>
                  <PermissionRoute resource="article" action="read">
                    <ArticleDashboard />
                  </PermissionRoute>
                </ProtectedRoute>
              }
            />
            <Route
              path="/article/:id"
              element={
                <ProtectedRoute>
                  <PermissionRoute resource="article" action="read">
                    <ArticleViewer />
                  </PermissionRoute>
                </ProtectedRoute>
              }
            />

            {/* ========== JUDGEMENT ========== */}
            <Route
              path="/judgement"
              element={
                <ProtectedRoute>
                  <PermissionRoute resource="judgement" action="read">
                    <JudgmentDashboard />
                  </PermissionRoute>
                </ProtectedRoute>
              }
            />
            <Route
              path="/judgment/add"
              element={
                <ProtectedRoute>
                  <PermissionRoute resource="judgement" action="create">
                    <JudgmentUploadModal />
                  </PermissionRoute>
                </ProtectedRoute>
              }
            />
            <Route
              path="/judgments/:id"
              element={
                <ProtectedRoute>
                  <PermissionRoute resource="judgement" action="read">
                    <JudgmentViewer />
                  </PermissionRoute>
                </ProtectedRoute>
              }
            />

            {/* ========== JUDGE ========== */}
            <Route
              path="/judge"
              element={
                <ProtectedRoute>
                  <PermissionRoute resource="judge" action="read">
                    <Judge />
                  </PermissionRoute>
                </ProtectedRoute>
              }
            />

            {/* ========== ACT ========== */}
            <Route
              path="/act"
              element={
                <ProtectedRoute>
                  <PermissionRoute resource="act" action="read">
                    <Act />
                  </PermissionRoute>
                </ProtectedRoute>
              }
            />

            {/* ========== JOURNAL ========== */}
            <Route
              path="/journal"
              element={
                <ProtectedRoute>
                  <PermissionRoute resource="journal" action="read">
                    <Journal />
                  </PermissionRoute>
                </ProtectedRoute>
              }
            />

            {/* ========== USER ========== */}
            <Route
              path="/user"
              element={
                <ProtectedRoute>
                  <PermissionRoute resource="user" action="read">
                    <User />
                  </PermissionRoute>
                </ProtectedRoute>
              }
            />

            {/* ========== ADMIN ========== */}
            <Route
              path="/admin/settings"
              element={
                <ProtectedRoute>
                  <AdminSettings />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/audit-log"
              element={
                <ProtectedRoute>
                  <AuditLog />
                </ProtectedRoute>
              }
            />

            {/* ========== FALLBACK ========== */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </main>
      </div>

      <Footer />
    </AuthContext.Provider>
  );
}